import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useCommonForm } from './hooks/useCommonForm';
import ErrorDisplay from './components/ErrorDisplay';
import MetadataForm from './components/MetadataForm';
import StateSelector from '@components/common/StateSelector';
import MetadataManager from '@utils/MetadataManager';
import { ImageManager } from '@components/attachments';
import FilterableSelect from '@components/common/FilterableSelect';
import { useCanCreateSharedResources } from '@hooks/usePermissions';
import api from '@services/api';
import './ResourceForm.scss';

const ResourceForm = ({ data, resource, onSubmit, onCancel, isEditing = false, loading: externalLoading = false, showAdvancedOptions = false }) => {
  const { t } = useLanguage();

  // Support both `data` (from common components) and `resource` (legacy)
  const modelData = data || resource;

  // Permission check for shared resource creation
  const { canCreateShared, loading: permissionsLoading } = useCanCreateSharedResources();

  // State declarations first
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [resourceScope, setResourceScope] = useState('shared'); // 'personal' or 'shared'
  const [reservationManagers, setReservationManagers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);

  // Initialize form with validation rules
  const initialFormData = {
    name: modelData?.name || '',
    description: modelData?.description || '',
    category_id: modelData?.category_id || '',
    location_id: modelData?.location_id || '',
    is_available: modelData?.is_available ?? true,
    notes: modelData?.notes || '',
    consumable: modelData?.consumable || false,
    personal: modelData?.personal || false,
    quantity: modelData?.quantity || 1,
    resource_metadata: modelData?.resource_metadata || [],
    allows_participation: modelData?.resource_metadata?.find(m => m.metadata_key === 'allows_participation')?.value === 'true',
    requires_reservation_validation: modelData?.resource_metadata?.find(m => m.metadata_key === 'reservation_managers')?.value === 'true'
  };

  // Dynamic validation rules that can access resourceScope
  const getValidationRules = () => ({
    name: {
      required: true,
      requiredMessage: t('resources.resourceForm.nameRequired') || 'Name is required',
      minLength: 1,
      minLengthMessage: t('resources.resourceForm.nameMinLength') || 'Name cannot be empty'
    },
    category_id: {
      required: true,
      requiredMessage: t('resources.resourceForm.categoryRequired') || 'Category is required'
    },
    location_id: {
      required: resourceScope === 'shared',
      requiredMessage: t('resources.resourceForm.locationRequired') || 'Location is required for shared resources'
    }
  });

  const {
    formData,
    loading,
    error,
    fieldErrors,
    handleChange,
    handleSubmit,
    updateFormData,
    getFieldProps,
    getCheckboxProps,
    handleError
  } = useCommonForm({
    initialData: initialFormData,
    onSubmit: async (submitData) => {
      // Extract metadata and feature flags from form data
      const { resource_metadata, allows_participation, requires_reservation_validation, ...resourceData } = submitData;

      // Validate personal resource constraints
      if (resourceScope === 'personal' && resourceData.location_id) {
        throw new Error(t('resources.resourceForm.personalLocationError') || 'Personal resources cannot have a location');
      }

      // Prepare data for submission - convert empty strings to null for optional fields
      const cleanedData = {
        ...resourceData,
        location_id: resourceScope === 'personal' ? null : (resourceData.location_id || null),
        personal: resourceScope === 'personal',
        quantity: resourceData.consumable ? (resourceData.quantity || 1) : undefined
      };

      try {
        // Call the original onSubmit with the cleaned resource data
        const result = await onSubmit(cleanedData);

        // If we have a resource ID (from creation or editing), sync metadata
        const resourceId = modelData?.id || result?.data?.id;
        if (resourceId) {
          // Prepare metadata array including allows_participation for non-consumable resources
          let metadataToSync = resource_metadata || [];

          // Add allows_participation to metadata if resource is non-consumable
          if (!cleanedData.consumable) {
            const participationMeta = {
              metadata_key: 'allows_participation',
              value: allows_participation ? 'true' : 'false'
            };
            // Remove any existing allows_participation entry
            metadataToSync = metadataToSync.filter(m => m.metadata_key !== 'allows_participation');
            // Add the new value
            metadataToSync.push(participationMeta);

            // Add reservation_managers metadata
            const reservationManagersMeta = {
              metadata_key: 'reservation_managers',
              value: requires_reservation_validation ? 'true' : 'false'
            };
            metadataToSync = metadataToSync.filter(m => m.metadata_key !== 'reservation_managers');
            metadataToSync.push(reservationManagersMeta);
          }

          if (metadataToSync.length > 0) {
            await MetadataManager.syncResourceMetadata(
              resourceId,
              metadataToSync,
              modelData?.resource_metadata || []
            );
          }

          // Sync reservation managers via API
          if (!cleanedData.consumable && requires_reservation_validation) {
            const managerUserIds = reservationManagers.map(m => m.user_id || m.id);
            await api.reservationManagers.setAll(resourceId, managerUserIds);
          } else if (!cleanedData.consumable && !requires_reservation_validation) {
            // Clear managers if validation is disabled
            await api.reservationManagers.setAll(resourceId, []);
          }
        }

        return result;
      } catch (error) {
        // If resource creation/update failed, don't attempt metadata sync
        throw error;
      }
    },
    externalLoading,
    getValidationRules
  });

  // Update form data when modelData changes
  useEffect(() => {
    if (modelData) {
      const newFormData = {
        name: modelData.name || '',
        description: modelData.description || '',
        category_id: modelData.category_id || '',
        location_id: modelData.location_id || '',
        is_available: modelData.is_available ?? true,
        notes: modelData.notes || '',
        consumable: modelData.consumable || false,
        personal: modelData.personal || false,
        quantity: modelData.quantity || 1,
        resource_metadata: modelData.resource_metadata || []
      };
      updateFormData(newFormData);

      // Set resource scope based on existing data and permissions
      // If user can't create shared resources, force personal
      if (!canCreateShared && !isEditing) {
        setResourceScope('personal');
      } else {
        setResourceScope(modelData.personal || !modelData.location_id ? 'personal' : 'shared');
      }
    }
  }, [modelData, canCreateShared, isEditing]); // Added canCreateShared and isEditing dependencies

  // Set default scope when permissions load (for new resources)
  useEffect(() => {
    if (!isEditing && !modelData && !permissionsLoading && canCreateShared !== undefined) {
      if (!canCreateShared) {
        setResourceScope('personal');
      }
    }
  }, [canCreateShared, permissionsLoading, isEditing, modelData]);

  useEffect(() => {
    const fetchOptions = async () => {
      setFormLoading(true);
      try {
        const [categoriesResponse, locationsResponse, usersResponse] = await Promise.all([
          api.get('/categories'),
          api.get('/locations'),
          api.get('/users?page_size=-1&is_active=true')
        ]);
        setCategories(categoriesResponse.data.models || []);
        setLocations(locationsResponse.data.models || []);
        setAllUsers(usersResponse.data.models || usersResponse.data || []);
      } catch (err) {
        console.error('Failed to fetch form options:', err);
        handleError(err, {
          showToast: true,
          setFormError: true,
          rethrow: false
        });
      } finally {
        setFormLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Load existing reservation managers when editing
  useEffect(() => {
    const loadManagers = async () => {
      if (isEditing && modelData?.id) {
        try {
          setManagersLoading(true);
          const response = await api.reservationManagers.getForResource(modelData.id);
          const managers = response.data?.managers || response.data || [];
          setReservationManagers(managers);
        } catch (err) {
          console.error('Failed to load reservation managers:', err);
        } finally {
          setManagersLoading(false);
        }
      }
    };
    loadManagers();
  }, [isEditing, modelData?.id]);

  // Handle metadata changes
  const handleMetadataChange = (metadataArray) => {
    handleChange({
      target: {
        name: 'resource_metadata',
        value: metadataArray
      }
    });
  };

  // Handle adding a reservation manager
  const handleAddManager = useCallback((userId) => {
    if (!userId) return;
    const userIdNum = parseInt(userId, 10);
    // Avoid duplicates
    if (reservationManagers.some(m => (m.user_id || m.id) === userIdNum)) return;
    const user = allUsers.find(u => u.id === userIdNum);
    if (user) {
      setReservationManagers(prev => [...prev, { user_id: user.id, user, id: user.id }]);
    }
  }, [allUsers, reservationManagers]);

  // Handle removing a reservation manager
  const handleRemoveManager = useCallback((userId) => {
    setReservationManagers(prev => prev.filter(m => (m.user_id || m.id) !== userId));
  }, []);

  return (
    <form className="sloot-form" onSubmit={handleSubmit}>
      <ErrorDisplay error={error} fieldErrors={fieldErrors} />

      <div className="form-group">
        <label htmlFor="name">{t('resources.resourceForm.name') || 'Name'} *</label>
        <input
          type="text"
          id="name"
          {...getFieldProps('name', {
            required: true,
            placeholder: t('resources.resourceForm.namePlaceholder') || 'Enter resource name'
          })}
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">{t('resources.resourceForm.description') || 'Description'}</label>
        <textarea
          id="description"
          {...getFieldProps('description', {
            placeholder: t('resources.resourceForm.descriptionPlaceholder') || 'Enter resource description (optional)',
            rows: 3
          })}
        />
      </div>

      {/* Resource scope section - only show if user can create shared resources or editing existing */}
      {(canCreateShared || isEditing) && (
        <div className="form-group">
          <label>{t('resources.resourceForm.resourceScope') || 'Resource Scope'}</label>
          <StateSelector
            options={[
              ...(canCreateShared || (isEditing && !modelData?.personal) ? [{
                value: 'shared',
                label: t('resources.resourceForm.shared') || 'Shared',
                color: 'primary'
              }] : []),
              {
                value: 'personal',
                label: t('resources.resourceForm.personal') || 'Personal',
                color: 'secondary'
              },
            ]}
            value={resourceScope}
            onChange={(value) => {
              setResourceScope(value);
              if (value === 'personal') {
                handleChange({ target: { name: 'location_id', value: '' } });
              }
            }}
            color="primary"
            size="medium"
            disabled={loading || (!canCreateShared && !isEditing)}
            tooltip={t('resources.resourceForm.scopeTooltip') || 'Personal resources belong to you and can be borrowed by everyone'}
          />
        </div>
      )}

      {/* Help message for personal resources or permission limitation */}
      {resourceScope === 'personal' && (
        <div className="form-help personal-resource-help">
          <p>
            {!canCreateShared && !isEditing
              ? (t('resources.resourceForm.personalOnlyHelp') || 'You can only create personal resources. Contact an administrator for shared resource permissions.')
              : (t('resources.resourceForm.personalResourceHelp') || 'This resource will belong to you and will be visible and borrowable by all users.')
            }
          </p>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="category_id">{t('resources.resourceForm.category') || 'Category'} *</label>
          <select
            id="category_id"
            {...getFieldProps('category_id', {
              required: true
            })}
          >
            <option value="">{t('resources.resourceForm.selectCategory') || 'Select a category'}</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Location - only if shared resource */}
        {resourceScope === 'shared' && (
          <div className="form-group">
            <label htmlFor="location_id">
              {t('resources.resourceForm.location') || 'Location'}
              {resourceScope === 'shared' ? ' *' : ''}
            </label>
            <select
              id="location_id"
              {...getFieldProps('location_id', {
                required: resourceScope === 'shared'
              })}
            >
              <option value="">{t('resources.resourceForm.selectLocation') || 'Select a location'}</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>{t('resources.resourceForm.resourceType') || 'Type de ressource'}</label>
          <StateSelector
            options={[
              {
                value: false,
                label: t('resources.resourceForm.unique') || 'Unique',
                color: 'secondary'
              },
              {
                value: true,
                label: t('resources.resourceForm.consumable') || 'Consommable',
                color: 'primary'
              }
            ]}
            value={formData.consumable}
            onChange={(value) => handleChange({ target: { name: 'consumable', type: 'checkbox', checked: value } })}
            color="primary"
            size="medium"
            disabled={loading}
            tooltip={t('resources.resourceForm.consumableTooltip') || 'Définit si la ressource est consommable ou unique'}
          />
        </div>

        {formData.consumable && (
          <div className="form-group">
            <label htmlFor="quantity">{t('resources.resourceForm.quantity') || 'Quantity'}</label>
            <input
              type="number"
              id="quantity"
              {...getFieldProps('quantity', {
                min: 0
              })}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label>{t('resources.resourceForm.availability') || 'Disponibilité'}</label>
        <StateSelector
          options={[
            {
              value: false,
              label: t('resources.resourceForm.unavailable') || 'Indisponible',
              color: 'error'
            },
            {
              value: true,
              label: t('resources.resourceForm.available') || 'Disponible',
              color: 'success'
            }
          ]}
          value={formData.is_available}
          onChange={(value) => handleChange({ target: { name: 'is_available', type: 'checkbox', checked: value } })}
          color="success"
          size="medium"
          disabled={loading}
          tooltip={t('resources.resourceForm.availabilityTooltip') || 'Définit si la ressource est disponible pour utilisation'}
        />
      </div>

      {/* Metadata Section */}
      <MetadataForm
        resourceId={modelData?.id || null}
        existingMetadata={formData.resource_metadata || []}
        isConsumable={formData.consumable}
        onChange={handleMetadataChange}
        disabled={loading}
        showAdvanced={false}
      />

      {/* Participation checkbox - only for non-consumable resources */}
      {!formData.consumable && (
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              {...getCheckboxProps('allows_participation')}
            />
            <span>{t('metadata.fields.allows_participation.label') || 'Allow Participation'}</span>
          </label>
          <p className="form-text">
            {t('metadata.fields.allows_participation.description') || 'Allow other users to indicate their participation in borrowings of this resource'}
          </p>
        </div>
      )}

      {/* Reservation Validation checkbox - only for non-consumable resources */}
      {!formData.consumable && (
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              {...getCheckboxProps('requires_reservation_validation')}
            />
            <span>{t('metadata.fields.reservation_managers.label') || 'Require Reservation Validation'}</span>
          </label>
          <p className="form-text">
            {t('metadata.fields.reservation_managers.description') || 'Reservations must be validated by designated managers before borrowing'}
          </p>

          {/* Manager list - only when validation is enabled */}
          {formData.requires_reservation_validation && (
            <div className="reservation-managers-section">
              <label>{t('metadata.fields.reservation_managers.managersLabel') || 'Reservation Managers'}</label>

              {/* Add manager select */}
              <FilterableSelect
                options={allUsers.filter(u => !reservationManagers.some(m => (m.user_id || m.id) === u.id))}
                value=""
                onChange={(selectedValue) => handleAddManager(selectedValue)}
                placeholder={t('metadata.fields.reservation_managers.addPlaceholder') || 'Add a manager...'}
                searchPlaceholder={t('metadata.fields.reservation_managers.searchPlaceholder') || 'Search users...'}
                className="manager-select"
                disabled={loading || managersLoading}
                clearable={false}
                getOptionLabel={(user) => user.name || user.email}
                getOptionValue={(user) => String(user.id)}
                noOptionsText={t('metadata.fields.reservation_managers.noUsersFound') || 'No users found'}
              />

              {/* Current managers list */}
              {reservationManagers.length > 0 && (
                <div className="managers-list">
                  {reservationManagers.map(manager => {
                    const managerUser = manager.user || allUsers.find(u => u.id === (manager.user_id || manager.id));
                    return (
                      <div key={manager.user_id || manager.id} className="manager-item">
                        <div className="manager-info">
                          <span className="manager-name">{managerUser?.name || managerUser?.email || `User #${manager.user_id || manager.id}`}</span>
                          {managerUser?.name && managerUser?.email && (
                            <span className="manager-email">{managerUser.email}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger manager-remove"
                          onClick={() => handleRemoveManager(manager.user_id || manager.id)}
                          disabled={loading}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {reservationManagers.length === 0 && (
                <p className="form-text form-text--warning">
                  {t('metadata.fields.reservation_managers.noManagers') || 'No managers assigned. Add at least one manager for validation to work.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Resource Images Section - only show if we have a resource ID (editing) */}
      {isEditing && modelData?.id && (
        <div className="form-group">
            <label>
                {t('categories.images')}
            </label>
          <ImageManager
            entity={modelData}
            editable={true}
            showUploader={true}
            maxImages={10}
            className="compact"
            thumbnailSize={80}
            maxFileSize={10 * 1024 * 1024} // 10MB for resource images
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="notes">{t('resources.resourceForm.notes') || 'Notes'}</label>
        <textarea
          id="notes"
          {...getFieldProps('notes', {
            placeholder: t('resources.resourceForm.notesPlaceholder') || 'Enter additional notes (optional)',
            rows: 3
          })}
        />
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={loading}
        >
          {t('common.cancel') || 'Cancel'}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? (t('common.saving') || 'Saving...') : (isEditing ? (t('common.update') || 'Update') : (t('common.create') || 'Create'))}
        </button>
      </div>
    </form>
  );
};

export default ResourceForm;
